import React, { useState } from 'react';
import { Calendar, CheckCircle, AlertTriangle, Clock, Filter, Download, Search, TrendingUp, XCircle } from 'lucide-react';

export default function TimelineValidation() {
  const [timelineFilter, setTimelineFilter] = useState('all');
  const [validationFilter, setValidationFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Mock timeline data
  const timelineEvents = [
    {
      id: 1,
      date: '2024-12-06',
      time: '09:30 AM',
      type: 'submission',
      title: 'Environmental Permit Renewal Submitted',
      description: 'Annual environmental permit renewal documentation submitted to regulatory authority',
      status: 'completed',
      category: 'environmental',
      officer: 'John Smith'
    },
    {
      id: 2,
      date: '2024-12-05',
      time: '02:15 PM',
      type: 'validation',
      title: 'Safety Compliance Validation Check',
      description: 'Automated validation passed for all safety compliance documents',
      status: 'completed',
      category: 'safety',
      officer: 'System'
    },
    {
      id: 3,
      date: '2024-12-04',
      time: '11:00 AM',
      type: 'alert',
      title: 'Fire Safety Certificate Expiring',
      description: 'Fire safety certificate will expire in 30 days - renewal required',
      status: 'pending',
      category: 'safety',
      officer: 'Sarah Johnson'
    },
    {
      id: 4,
      date: '2024-12-03',
      time: '04:45 PM',
      type: 'upload',
      title: 'New Quality Standard Documentation',
      description: 'ISO 9001:2015 certification documents uploaded and verified',
      status: 'completed',
      category: 'quality',
      officer: 'Mike Chen'
    },
    {
      id: 5,
      date: '2024-12-02',
      time: '10:20 AM',
      type: 'validation',
      title: 'Operational License Validation Failed',
      description: 'Missing required signatures on operational license renewal form',
      status: 'failed',
      category: 'operational',
      officer: 'System'
    }
  ];

  // Mock validation checks data
  const validationChecks = [
    {
      id: 1,
      category: 'Document Completeness',
      status: 'passed',
      score: 98,
      checks: 45,
      passed: 44,
      failed: 1,
      lastRun: '2024-12-06 08:00 AM',
      issues: ['Missing signature on Form-234']
    },
    {
      id: 2,
      category: 'Expiry Date Validation',
      status: 'passed',
      score: 100,
      checks: 32,
      passed: 32,
      failed: 0,
      lastRun: '2024-12-06 08:00 AM',
      issues: []
    },
    {
      id: 3,
      category: 'Regulatory Compliance',
      status: 'warning',
      score: 87,
      checks: 28,
      passed: 24,
      failed: 4,
      lastRun: '2024-12-06 08:00 AM',
      issues: ['EPA-2024 pending approval', 'OSHA-156 requires update']
    },
    {
      id: 4,
      category: 'Data Integrity',
      status: 'passed',
      score: 96,
      checks: 52,
      passed: 50,
      failed: 2,
      lastRun: '2024-12-06 08:00 AM',
      issues: ['Checksum mismatch on 2 documents']
    },
    {
      id: 5,
      category: 'Version Control',
      status: 'passed',
      score: 100,
      checks: 38,
      passed: 38,
      failed: 0,
      lastRun: '2024-12-06 08:00 AM',
      issues: []
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
      case 'passed':
        return 'text-green-600 bg-green-100';
      case 'pending': case'warning':
        return 'text-orange-600 bg-orange-100';
      case 'failed':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'submission':
        return <CheckCircle size={20} />;
      case 'validation':
        return <TrendingUp size={20} />;
      case 'alert':
        return <AlertTriangle size={20} />;
      case 'upload':
        return <Calendar size={20} />;
      default:
        return <Clock size={20} />;
    }
  };

  const filteredEvents = timelineEvents?.filter(event => {
    const matchesFilter = timelineFilter === 'all' || event?.type === timelineFilter;
    const matchesSearch = searchTerm === '' || 
      event?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      event?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const filteredValidations = validationChecks?.filter(check => {
    return validationFilter === 'all' || check?.status === validationFilter;
  });

  return (
    <div className="space-y-6">
      {/* Validation Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Overall Score</p>
              <p className="text-2xl font-bold text-green-600">96.2%</p>
              <p className="text-xs text-gray-500 mt-1">+2.3% from last check</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircle className="text-green-600" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Checks Passed</p>
              <p className="text-2xl font-bold text-gray-900">188/195</p>
              <p className="text-xs text-gray-500 mt-1">7 issues to resolve</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <TrendingUp className="text-blue-600" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Critical Issues</p>
              <p className="text-2xl font-bold text-orange-600">4</p>
              <p className="text-xs text-gray-500 mt-1">Requires attention</p>
            </div>
            <div className="p-3 bg-orange-100 rounded-lg">
              <AlertTriangle className="text-orange-600" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Last Validated</p>
              <p className="text-lg font-bold text-gray-900">Today</p>
              <p className="text-xs text-gray-500 mt-1">08:00 AM</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <Clock className="text-purple-600" size={24} />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Timeline Section */}
        <div className="bg-white rounded-lg shadow border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Compliance Timeline</h3>
              <button className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                <Download size={16} />
                Export
              </button>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="text"
                  placeholder="Search events..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <select
                value={timelineFilter}
                onChange={(e) => setTimelineFilter(e?.target?.value)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Events</option>
                <option value="submission">Submissions</option>
                <option value="validation">Validations</option>
                <option value="alert">Alerts</option>
                <option value="upload">Uploads</option>
              </select>
            </div>
          </div>

          <div className="p-4 max-h-[600px] overflow-y-auto">
            <div className="space-y-4">
              {filteredEvents?.map((event, index) => (
                <div key={event?.id} className="relative">
                  {index !== filteredEvents?.length - 1 && (
                    <div className="absolute left-5 top-12 bottom-0 w-0.5 bg-gray-200" />
                  )}
                  
                  <div className="flex gap-4">
                    <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${getStatusColor(event?.status)}`}>
                      {getTypeIcon(event?.type)}
                    </div>

                    <div className="flex-1 bg-gray-50 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="font-semibold text-gray-900">{event?.title}</h4>
                          <p className="text-sm text-gray-600 mt-1">{event?.description}</p>
                        </div>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(event?.status)}`}>
                          {event?.status}
                        </span>
                      </div>

                      <div className="flex items-center gap-4 text-xs text-gray-500 mt-3">
                        <span className="flex items-center gap-1">
                          <Calendar size={14} />
                          {event?.date}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock size={14} />
                          {event?.time}
                        </span>
                        <span className="px-2 py-1 bg-white rounded border border-gray-200">
                          {event?.category}
                        </span>
                        <span>By: {event?.officer}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Validation Checks Section */}
        <div className="bg-white rounded-lg shadow border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Validation Checks</h3>
              <button className="flex items-center gap-2 px-3 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700">
                <TrendingUp size={16} />
                Run All Checks
              </button>
            </div>

            <div className="flex gap-3">
              <select
                value={validationFilter}
                onChange={(e) => setValidationFilter(e?.target?.value)}
                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Checks</option>
                <option value="passed">Passed</option>
                <option value="warning">Warnings</option>
                <option value="failed">Failed</option>
              </select>
            </div>
          </div>

          <div className="p-4 max-h-[600px] overflow-y-auto">
            <div className="space-y-4">
              {filteredValidations?.map((check) => (
                <div key={check?.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900">{check?.category}</h4>
                      <p className="text-xs text-gray-500 mt-1">Last run: {check?.lastRun}</p>
                    </div>
                    <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(check?.status)}`}>
                      {check?.status}
                    </span>
                  </div>

                  <div className="mb-3">
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-gray-600">Validation Score</span>
                      <span className="font-semibold text-gray-900">{check?.score}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${
                          check?.score >= 95 ? 'bg-green-500' :
                          check?.score >= 85 ? 'bg-orange-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${check?.score}%` }}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2 mb-3">
                    <div className="text-center p-2 bg-white rounded border border-gray-200">
                      <p className="text-xs text-gray-600">Total</p>
                      <p className="text-lg font-bold text-gray-900">{check?.checks}</p>
                    </div>
                    <div className="text-center p-2 bg-green-50 rounded border border-green-200">
                      <p className="text-xs text-green-600">Passed</p>
                      <p className="text-lg font-bold text-green-600">{check?.passed}</p>
                    </div>
                    <div className="text-center p-2 bg-red-50 rounded border border-red-200">
                      <p className="text-xs text-red-600">Failed</p>
                      <p className="text-lg font-bold text-red-600">{check?.failed}</p>
                    </div>
                  </div>

                  {check?.issues?.length > 0 && (
                    <div className="bg-orange-50 border border-orange-200 rounded p-3">
                      <p className="text-xs font-semibold text-orange-900 mb-2">Issues Found:</p>
                      <ul className="space-y-1">
                        {check?.issues?.map((issue, idx) => (
                          <li key={idx} className="text-xs text-orange-800 flex items-start gap-2">
                            <XCircle size={14} className="flex-shrink-0 mt-0.5" />
                            <span>{issue}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
        <div className="flex flex-wrap gap-3">
          <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            <TrendingUp size={16} />
            Schedule Validation
          </button>
          <button className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
            <Download size={16} />
            Export Timeline
          </button>
          <button className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
            <Filter size={16} />
            Advanced Filters
          </button>
          <button className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
            <CheckCircle size={16} />
            Generate Report
          </button>
        </div>
      </div>
    </div>
  );
}